import React, { useEffect, useRef, useState } from 'react';
import { ArrowRightLeft, CheckCircle2, Loader2, MapPin, ScanLine, Undo2, XCircle } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Progress } from '@/components/ui/progress';
import { toast } from 'sonner';
import { JwStatusBadge } from './JwStatusBadge';
import { acceptOrder, errText, pickBundle, rejectOrder, transferOrder, unpickBundle } from '@/services/tissueJwApi';

const LineProgress = ({ order }) => (
  <div className="space-y-2">
    {order.lines.map((l) => {
      const picked = (order.pick_bundles || []).filter((b) => b.picked && b.sort_no === l.sort_no).reduce((a, b) => a + b.pcs, 0);
      return (
        <div key={l.sort_no} data-testid={`gs-line-${l.sort_no}`}>
          <div className="flex justify-between gap-2 text-sm"><span className="font-mono truncate min-w-0">{l.sort_no} → {l.fg_name}</span>
            <span data-testid={`gs-line-picked-${l.sort_no}`}>{picked} / {l.requested_pcs} pcs</span></div>
          <Progress value={Math.min(100, (picked / l.requested_pcs) * 100)} className="h-2" />
        </div>
      );
    })}
  </div>
);

const SlotList = ({ order, onUnpick }) => (
  <div className="border rounded-lg divide-y bg-white max-h-[340px] overflow-y-auto">
    {(order.pick_bundles || []).map((b) => (
      <div key={b.bundle_barcode} className={`flex items-center justify-between px-3 py-2 ${b.picked ? 'bg-emerald-50/60' : ''}`} data-testid={`gs-slot-${b.bundle_barcode}`}>
        <div className="min-w-0">
          <p className="font-mono text-sm break-all">{b.bundle_barcode} <span className="text-gray-400">· {b.sort_no} · {b.pcs} pcs</span></p>
          <p className="text-[11px] text-gray-500 flex items-center gap-1"><MapPin className="w-3 h-3" />{b.footprint || 'no location'}
            {b.substituted_from && <span className="ml-1 text-amber-700">· picked instead of {b.substituted_from}</span>}</p>
        </div>
        {b.picked ? (
          <Button size="sm" variant="ghost" onClick={() => onUnpick(b.bundle_barcode)} data-testid={`gs-unpick-${b.bundle_barcode}`}>
            <Undo2 className="w-3.5 h-3.5 mr-1" /> Undo</Button>
        ) : <span className="text-[11px] text-gray-400">to pick</span>}
      </div>
    ))}
  </div>
);

const RequestActions = ({ order, run }) => {
  const [reason, setReason] = useState('');
  return (
    <div className="flex flex-wrap gap-2 items-center">
      <Button onClick={() => run('accept', () => acceptOrder(order.jw_id), 'Accepted — pick slip ready', 'picking')} className="bg-[#2D5F4F] hover:bg-[#234A3D]" data-testid="gs-accept-btn">
        <CheckCircle2 className="w-4 h-4 mr-2" /> Accept & make pick slip</Button>
      <Input value={reason} onChange={(e) => setReason(e.target.value)} placeholder="Reason to reject" className="w-full sm:w-[220px]" data-testid="gs-reject-reason" />
      <Button variant="outline" className="text-rose-700" disabled={!reason.trim()} onClick={() => run('reject', () => rejectOrder(order.jw_id, reason), 'Request rejected')} data-testid="gs-reject-btn">
        <XCircle className="w-4 h-4 mr-2" /> Reject</Button>
    </div>
  );
};

// Greige Storage side of a Tissue job work request: accept → pick → Internal Transfer to Tissue.
export const GsPickPanel = ({ order, onChanged }) => {
  const [scan, setScan] = useState('');
  const [busy, setBusy] = useState('');
  const ref = useRef(null);
  useEffect(() => { if (order.status === 'PICKING') setTimeout(() => ref.current?.focus(), 80); }, [order.jw_id, order.status]);

  const run = async (key, fn, msg, nextTab) => {
    setBusy(key);
    try { const r = await fn(); if (msg) toast.success(typeof msg === 'function' ? msg(r) : msg); (r.warnings || []).forEach((w) => toast.warning(w)); onChanged(nextTab); }
    catch (e) { toast.error(errText(e)); } finally { setBusy(''); setTimeout(() => ref.current?.focus(), 80); }
  };
  const onScan = (e) => { e.preventDefault(); const code = scan.trim(); if (!code) return; setScan('');
    run('pick', () => pickBundle(order.jw_id, code), (r) => `${r.bundle_code} picked (${r.pcs} pcs)${r.substituted_from ? ` instead of ${r.substituted_from}` : ''}`); };
  const picked = (order.pick_bundles || []).filter((b) => b.picked);

  return (
    <Card data-testid="gs-pick-panel">
      <CardHeader className="flex flex-row flex-wrap items-center justify-between gap-3">
        <div><CardTitle className="text-base font-mono">{order.jw_id}</CardTitle>
          <p className="text-xs text-gray-500">Tissue · jobber {order.jobber_name} · by {order.created_staff_name || order.created_by_name}</p></div>
        <JwStatusBadge status={order.status} testId="gs-order-status" />
      </CardHeader>
      <CardContent className="space-y-4">
        <LineProgress order={order} />
        {order.status === 'REQUESTED' && <RequestActions order={order} run={run} />}
        {order.status === 'PICKING' && (<>
          <form onSubmit={onScan} className="flex gap-2 items-center">
            <ScanLine className="w-5 h-5 text-gray-500" />
            <Input ref={ref} value={scan} onChange={(e) => setScan(e.target.value)} placeholder="Scan bundle barcode" disabled={busy === 'pick'} className="h-11 flex-1 min-w-0 sm:max-w-md" data-testid="gs-pick-input" />
            <Button type="submit" disabled={busy === 'pick'} data-testid="gs-pick-submit">{busy === 'pick' ? <Loader2 className="w-4 h-4 animate-spin" /> : 'Pick'}</Button>
          </form>
          {(order.stock_short || []).map((s) => <p key={s} className="text-xs text-amber-700">Short stock — {s}</p>)}
          <SlotList order={order} onUnpick={(c) => run('unpick', () => unpickBundle(order.jw_id, c), `${c} put back`)} />
          <Button disabled={!picked.length || busy === 'transfer'} onClick={() => run('transfer', () => transferOrder(order.jw_id), (r) => `Internal Transfer ${r.transfer_id} made — ${r.bundles} bundle(s) to Tissue`, 'done')}
            className="bg-[#1A1F2E] hover:bg-black w-full sm:w-auto h-auto py-2 whitespace-normal" data-testid="gs-transfer-btn">
            {busy === 'transfer' ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <ArrowRightLeft className="w-4 h-4 mr-2" />}
            Make Internal Transfer to Tissue ({picked.length} bundle(s))</Button>
        </>)}
        {order.transfer_id && <p className="text-sm text-emerald-700" data-testid="gs-transfer-id">Internal Transfer {order.transfer_id} · {order.sent_pcs} pcs — send through Gate Outward as Job Worker with the e-way bill.</p>}
      </CardContent>
    </Card>
  );
};

export default GsPickPanel;
